import { computed, toValue } from 'vue';
import { isNil, isObject, isUndefined } from 'lodash';

import { DatabaseEntities, Settings, makeEntitySettings } from '@nova/core';

import type { MaybeRefOrGetter } from 'vue';
import type { ISettings, IWarehouse, Maybe } from '@nova/core';
import type { OrgDetails } from '@satellite/composables/useWarehousesPresenter';

/**
 * Minimal warehouse shape needed for settings inheritance. Lean appointment
 * joins (`IWarehouse` on dock) and full presenter warehouses both satisfy this.
 */
export type WarehouseSettingsFields = Pick<IWarehouse, 'id' | 'orgId' | 'settings' | 'org'>;

export type WarehouseSettingsSource = WarehouseSettingsFields | null;

export type ConsolidateWarehouseSettingsOptions = {
  /**
   * Explicit org when not nested on the warehouse (prefer `warehouse.org` from
   * joins like `dock.warehouse.org`).
   */
  org?: Maybe<OrgDetails>;
  /**
   * When true (default), resolve warehouse → org → schema defaults via makeEntitySettings.
   * When false, return a clone of warehouse.settings only (no inherit / no defaults fill).
   */
  considerHierarchy?: boolean;
  /**
   * When true, warn if consolidation runs without a usable warehouse / `org.settings`
   * (settings then fall back to warehouse + schema defaults, which may be wrong).
   * Off by default so flows that legitimately consolidate before a warehouse/org
   * is loaded stay quiet; opt in for flows that require full inheritance (e.g. the
   * appointment status manager).
   */
  warnOnMissingOrg?: boolean;
};

export type WarehouseSettingHelperOptions = {
  considerHierarchy?: boolean;
};

export type UseWarehouseSettingsOptions = {
  /**
   * Optional org override when the warehouse payload does not nest `org`.
   */
  org?: MaybeRefOrGetter<Maybe<OrgDetails>>;
  /**
   * Forwarded to {@link consolidateWarehouseSettings}. Off by default; opt in
   * for flows that require full org inheritance to resolve correctly.
   */
  warnOnMissingOrg?: MaybeRefOrGetter<boolean>;
};

/**
 * Pure warehouse settings consolidation. Clones settings before makeEntitySettings
 * so the caller's warehouse / cache object is never mutated.
 */
export function consolidateWarehouseSettings(
  warehouse: WarehouseSettingsSource | undefined,
  options: ConsolidateWarehouseSettingsOptions = {}
): ISettings {
  const { considerHierarchy = true, warnOnMissingOrg = false } = options;

  if (isNil(warehouse)) {
    if (warnOnMissingOrg) {
      console.warn('[useWarehouseSettings] warehouse is required');
    }
    return {};
  }

  const rawSettings = { ...(warehouse.settings ?? {}) };

  if (!considerHierarchy) {
    return rawSettings;
  }

  const resolvedOrg = options.org ?? warehouse.org ?? null;
  const hasOrgSettings =
    !isNil(resolvedOrg) && isObject(resolvedOrg.settings) && !Array.isArray(resolvedOrg.settings);

  /**
   * If we don't have the org settings, warn so settings will fall back to warehouse settings + schema defaults,
   * which may not be what we want. Gated behind `warnOnMissingOrg` so flows that
   * consolidate before org is loaded don't spam the console.
   */
  if (!hasOrgSettings && warnOnMissingOrg) {
    console.warn('[useWarehouseSettings] hierarchy requested but org.settings is missing', {
      warehouseId: warehouse.id ?? null,
      orgId: warehouse.orgId
    });
  }

  const warehouseForSettings = {
    ...warehouse,
    settings: rawSettings,
    org: resolvedOrg
  };

  return makeEntitySettings(DatabaseEntities.Warehouse, warehouseForSettings);
}

function readSettingValue(
  settingKey: string,
  settings: ISettings | null | undefined,
  providedDefaultValue: unknown = null
): unknown {
  const fromSettings = settings?.[settingKey as keyof ISettings];

  if (!isNil(fromSettings)) {
    return fromSettings;
  }

  if (!isNil(providedDefaultValue)) {
    return providedDefaultValue;
  }

  const schemaDefaultValue = Settings[settingKey]?.defaultValue;
  if (!isUndefined(schemaDefaultValue)) {
    return schemaDefaultValue;
  }

  return providedDefaultValue;
}

/**
 * Vuex-free warehouse settings reader. Prefer consolidated (hierarchy) values by default.
 * `warehouse` is required — there is no meaningful settings resolution without one.
 * App-specific semantic helpers (military time, ref-num label, …) live in each app façade.
 */
export function useWarehouseSettings(
  warehouse: MaybeRefOrGetter<WarehouseSettingsFields | null | undefined>,
  options: UseWarehouseSettingsOptions = {}
) {
  const warehouseRef = computed(() => toValue(warehouse));
  const optionOrgRef = computed(() => toValue(options.org) ?? null);
  const warnOnMissingOrgRef = computed(() => toValue(options.warnOnMissingOrg) ?? false);

  const rawSettings = computed<ISettings>(() => ({
    ...(warehouseRef.value?.settings ?? {})
  }));

  const consolidatedSettings = computed<ISettings>(() =>
    consolidateWarehouseSettings(warehouseRef.value, {
      org: optionOrgRef.value,
      considerHierarchy: true,
      warnOnMissingOrg: warnOnMissingOrgRef.value
    })
  );

  const resolveSettings = (helperOptions?: WarehouseSettingHelperOptions): ISettings => {
    const considerHierarchy = helperOptions?.considerHierarchy ?? true;

    if (!considerHierarchy) {
      return rawSettings.value;
    }

    return consolidatedSettings.value;
  };

  const getSettingValue = (
    settingKey: string,
    helperOptions?: WarehouseSettingHelperOptions & { defaultValue?: unknown }
  ) => {
    return readSettingValue(
      settingKey,
      resolveSettings(helperOptions),
      helperOptions?.defaultValue ?? null
    );
  };

  return {
    rawSettings,
    consolidatedSettings,
    getSettingValue
  };
}
